import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link } from 'react-router-dom';
import { Lightbulb, Rocket, Users, Briefcase } from 'lucide-react';
import logo from '../assets/BCL_nobg_crop.png';
import './Careers.css';

const jobOpenings = [
  {
    id: 1,
    title: 'Frontend Developer',
    icon: <CodeIcon sx={{ fontSize: '2.5rem', color: '#51A8B1' }} />,
    department: 'Engineering',
    location: 'Udaipur, Rajasthan',
    type: 'Full-time',
    openings: 1,
    description: 'We are seeking a talented Frontend Developer to build intuitive and responsive user interfaces for our healthcare IT solutions.',
    responsibilities: [
      'Develop and maintain responsive web applications using React.js and modern frontend technologies',
      'Collaborate with UX/UI designers to implement pixel-perfect designs',
      'Integrate RESTful APIs and ensure seamless data flow between frontend and backend',
      'Optimize applications for maximum speed and scalability',
      'Write clean, maintainable, and well-documented code',
      'Participate in code reviews and contribute to technical discussions',
      'Stay updated with emerging frontend technologies and best practices'
    ],
    requirements: [
      'Bachelor\'s degree in Computer Science or related field',
      '2-4 years of experience in frontend development',
      'Strong proficiency in React.js, JavaScript (ES6+), HTML5, and CSS3',
      'Experience with Material-UI or similar component libraries',
      'Understanding of responsive design and cross-browser compatibility',
      'Familiarity with version control systems (Git)',
      'Knowledge of healthcare IT standards (HL7, FHIR) is a plus',
      'Excellent problem-solving skills and attention to detail'
    ],
    benefits: [
      'Competitive salary and performance bonuses',
      'Health insurance coverage',
      'Flexible working hours',
      'Professional development opportunities',
      'Collaborative and innovative work environment'
    ]
  },
  {
    id: 2,
    title: 'Backend Developer',
    icon: <StorageIcon sx={{ fontSize: '2.5rem', color: '#51A8B1' }} />,
    department: 'Engineering',
    location: 'Udaipur, Rajasthan',
    type: 'Full-time',
    openings: 2,
    description: 'Join our backend team to architect and build scalable, secure healthcare systems that process critical medical data.',
    responsibilities: [
      'Design and develop robust backend APIs and microservices for healthcare applications',
      'Implement secure data handling practices ensuring HIPAA compliance',
      'Optimize database queries and improve system performance',
      'Integrate with healthcare standards like HL7, FHIR, and DICOM',
      'Collaborate with frontend developers to design efficient API contracts',
      'Implement automated testing and continuous integration practices',
      'Monitor system performance and troubleshoot production issues'
    ],
    requirements: [
      'Bachelor\'s degree in Computer Science or related field',
      '3-5 years of backend development experience',
      'Strong proficiency in Python, Node.js, or Java',
      'Experience with SQL and NoSQL databases (PostgreSQL, MongoDB)',
      'Understanding of RESTful API design and microservices architecture',
      'Knowledge of healthcare data standards (HL7, FHIR) highly preferred',
      'Experience with cloud platforms (AWS, Azure, or GCP)',
      'Understanding of security best practices and data encryption'
    ],
    benefits: [
      'Competitive salary and performance bonuses',
      'Health insurance coverage',
      'Remote work options',
      'Learning and certification support',
      'Career growth opportunities'
    ]
  },
  {
    id: 3,
    title: 'Software Demonstration Specialist',
    icon: <PresentToAllIcon sx={{ fontSize: '2.5rem', color: '#51A8B1' }} />,
    department: 'Sales & Customer Success',
    location: 'Udaipur, Rajasthan',
    type: 'Full-time',
    openings: 2,
    description: 'We need enthusiastic professionals to demonstrate our healthcare IT solutions to potential clients and support implementation.',
    responsibilities: [
      'Conduct live product demonstrations to healthcare organizations and stakeholders',
      'Understand client requirements and showcase relevant software features',
      'Provide pre-sales technical support and answer product-related queries',
      'Assist with proof-of-concept implementations and trial setups',
      'Create compelling presentation materials and demo scenarios',
      'Gather client feedback and communicate requirements to development team',
      'Support onboarding and training for new customers',
      'Stay updated on product features and healthcare industry trends'
    ],
    requirements: [
      'Bachelor\'s degree in any field (Computer Science, Healthcare Management, or related fields preferred)',
      '1-3 years of experience in software demonstration, sales, or customer-facing roles',
      'Excellent presentation and communication skills',
      'Strong understanding of healthcare workflows and terminology',
      'Ability to quickly learn and demonstrate complex software systems',
      'Technical aptitude with ability to troubleshoot basic issues',
      'Experience with CRM tools and demonstration software',
      'Willingness to travel to client locations as needed'
    ],
    benefits: [
      'Competitive salary with incentive structure',
      'Health insurance coverage',
      'Travel allowances',
      'Professional development and training',
      'Opportunity to work with cutting-edge healthcare technology'
    ]
  }
];

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => setSelectedJob(null), 200);
  };

  return (
    <Box className="careers-section" sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              fontWeight: 700,
              color: '#51A8B1',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Join Our Team
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{
              fontFamily: 'Quicksand, sans-serif',
              color: '#666',
              mb: 8,
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.8,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              px: 2,
            }}
          >
            Be part of a team that's revolutionizing healthcare technology. 
            We're always looking for passionate individuals who share our 
            commitment to excellence and innovation.
          </Typography>
        </motion.div>

        {/* Current Openings Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontFamily: 'Barlow, sans-serif',
              fontWeight: 700,
              color: '#51A8B1',
              mb: 6,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            Current Openings
          </Typography>

          {/* Job Cards Container - Using Flexbox for 3x1 Grid */}
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              width: '100%',
              alignItems: 'stretch'
            }}
          >
            {jobOpenings.map((job, index) => (
              <Box
                key={job.id}
                sx={{
                  flex: { xs: '0 0 auto', md: '1 1 0' },
                  width: { xs: '100%', md: 'calc(33.333% - 21.333px)' },
                  minWidth: 0
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  whileHover={{ y: -8 }}
                  style={{ height: '100%' }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      minHeight: '600px',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 4,
                      background: 'linear-gradient(145deg, #ffffff 0%, #f9fafb 100%)',
                      border: '1px solid rgba(81, 168, 177, 0.1)',
                      boxShadow: '0 10px 40px rgba(81, 168, 177, 0.1)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 15px 50px rgba(81, 168, 177, 0.2)',
                        borderColor: 'rgba(81, 168, 177, 0.3)',
                      },
                    }}
                    onClick={() => handleOpenDialog(job)}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(81, 168, 177, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      {job.icon}
                    </Box>

                    {/* Job Title */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: 'Barlow, sans-serif',
                        fontWeight: 700,
                        color: '#51A8B1',
                        mb: 1,
                      }}
                    >
                      {job.title}
                    </Typography>

                    {/* Department */}
                    <Chip
                      label={job.department}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(168, 207, 69, 0.1)',
                        color: '#A8CF45',
                        fontFamily: 'Quicksand, sans-serif',
                        fontWeight: 600,
                        mb: 2,
                        alignSelf: 'flex-start',
                      }}
                    />

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.7,
                        minHeight: '90px',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {job.description}
                    </Typography>

                    {/* Job Details */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 'auto', pb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: '#666',
                            fontSize: '0.9rem',
                          }}
                        >
                          {job.location}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: '#666',
                            fontSize: '0.9rem',
                          }}
                        >
                          {job.type}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: '#666',
                            fontSize: '0.9rem',
                          }}
                        >
                          {job.openings} {job.openings === 1 ? 'Opening' : 'Openings'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Apply Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        background: 'linear-gradient(135deg, #51A8B1 0%, #459199 100%)',
                        color: 'white',
                        fontFamily: 'Barlow, sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        padding: '12px',
                        borderRadius: 2.5,
                        textTransform: 'none',
                        boxShadow: '0 4px 15px rgba(81, 168, 177, 0.3)',
                        mt: 'auto',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #459199 0%, #3a7d85 100%)',
                          boxShadow: '0 6px 20px rgba(81, 168, 177, 0.4)',
                        },
                      }}
                    >
                      View Details
                    </Button>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </motion.div>

        {/* Why Join Us Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Paper
            elevation={2}
            sx={{
              p: { xs: 4, md: 6 },
              mt: 8,
              borderRadius: 4,
              background: 'linear-gradient(145deg, #ffffff 0%, #f0f9fa 100%)',
              border: '2px solid rgba(81, 168, 177, 0.15)',
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                color: '#51A8B1',
                mb: 4,
              }}
            >
              Why Join BioCircuit LABS?
            </Typography>
            
            {/* Benefits Container - Using Flexbox */}
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'wrap',
                gap: 4,
                justifyContent: 'center'
              }}
            >
              {/* Innovation First */}
              <Box sx={{ 
                flex: { xs: '1 1 100%', md: '0 1 calc(50% - 16px)' },
                textAlign: 'center', 
                px: 2 
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(81, 168, 177, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <Lightbulb size={40} color="#51A8B1" strokeWidth={2} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 1,
                    fontSize: '1.1rem',
                  }}
                >
                  Innovation First
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: '#666',
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                  }}
                >
                  Work with cutting-edge technologies and contribute to revolutionary healthcare solutions
                </Typography>
              </Box>

              {/* Career Growth */}
              <Box sx={{ 
                flex: { xs: '1 1 100%', md: '0 1 calc(50% - 16px)' },
                textAlign: 'center', 
                px: 2 
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(81, 168, 177, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <Rocket size={40} color="#51A8B1" strokeWidth={2} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 1,
                    fontSize: '1.1rem',
                  }}
                >
                  Career Growth
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: '#666',
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                  }}
                >
                  Continuous learning opportunities and clear paths for professional advancement
                </Typography>
              </Box>

              {/* Collaborative Culture */}
              <Box sx={{ 
                flex: { xs: '1 1 100%', md: '0 1 calc(50% - 16px)' },
                textAlign: 'center', 
                px: 2 
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(81, 168, 177, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <Users size={40} color="#51A8B1" strokeWidth={2} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 1,
                    fontSize: '1.1rem',
                  }}
                >
                  Collaborative Culture
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: '#666',
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                  }}
                >
                  Join a supportive team where your ideas matter and collaboration thrives
                </Typography>
              </Box>

              {/* Work-Life Balance */}
              <Box sx={{ 
                flex: { xs: '1 1 100%', md: '0 1 calc(50% - 16px)' },
                textAlign: 'center', 
                px: 2 
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(81, 168, 177, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <Briefcase size={40} color="#51A8B1" strokeWidth={2} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 1,
                    fontSize: '1.1rem',
                  }}
                >
                  Work-Life Balance
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: '#666',
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                  }}
                >
                  Flexible working hours and remote options to maintain a healthy work-life balance
                </Typography>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      {/* Job Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxHeight: '90vh',
          },
        }}
      >
        {selectedJob && (
          <>
            <DialogTitle
              sx={{
                background: 'linear-gradient(135deg, #51A8B1 0%, #459199 100%)',
                color: 'white',
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 700,
                fontSize: '1.75rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {selectedJob.title}
              <IconButton
                onClick={handleCloseDialog}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 3 }}>
              {/* Job Overview */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 2,
                  }}
                >
                  Job Overview
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: '#666',
                    lineHeight: 1.8,
                    mb: 2,
                  }}
                >
                  {selectedJob.description}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <Chip
                    icon={<LocationOnIcon />}
                    label={selectedJob.location}
                    sx={{
                      fontFamily: 'Quicksand, sans-serif',
                      backgroundColor: 'rgba(81, 168, 177, 0.1)',
                      color: '#51A8B1',
                    }}
                  />
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={selectedJob.type}
                    sx={{
                      fontFamily: 'Quicksand, sans-serif',
                      backgroundColor: 'rgba(81, 168, 177, 0.1)',
                      color: '#51A8B1',
                    }}
                  />
                  <Chip
                    icon={<PeopleIcon />}
                    label={`${selectedJob.openings} ${selectedJob.openings === 1 ? 'Opening' : 'Openings'}`}
                    sx={{
                      fontFamily: 'Quicksand, sans-serif',
                      backgroundColor: 'rgba(168, 207, 69, 0.1)',
                      color: '#A8CF45',
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Responsibilities */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 2,
                  }}
                >
                  Key Responsibilities
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  {selectedJob.responsibilities.map((item, index) => (
                    <Typography
                      component="li"
                      key={index}
                      sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        mb: 1,
                        lineHeight: 1.8,
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Requirements */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 2,
                  }}
                >
                  Requirements
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  {selectedJob.requirements.map((item, index) => (
                    <Typography
                      component="li"
                      key={index}
                      sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        mb: 1,
                        lineHeight: 1.8,
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Benefits */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontWeight: 600,
                    color: '#51A8B1',
                    mb: 2,
                  }}
                >
                  Benefits
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  {selectedJob.benefits.map((item, index) => (
                    <Typography
                      component="li"
                      key={index}
                      sx={{
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        mb: 1,
                        lineHeight: 1.8,
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={handleCloseDialog}
                sx={{
                  fontFamily: 'Barlow, sans-serif',
                  fontWeight: 600,
                  color: '#666',
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #51A8B1 0%, #459199 100%)',
                  color: 'white',
                  fontFamily: 'Barlow, sans-serif',
                  fontWeight: 600,
                  padding: '10px 30px',
                  borderRadius: 2.5,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #459199 0%, #3a7d85 100%)',
                  },
                }}
              >
                Apply Now
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Footer Section */}
      <Box 
        component="footer" 
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(81, 168, 177, 0.1)',
          pt: 8,
          pb: 4,
          mt: 12
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
            {/* Logo and Company Info */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={logo} 
                    alt="BioCircuit Labs" 
                    style={{ height: '80px', marginRight: '15px' }}
                  />
                </motion.div>
                <Box>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#51A8B1',
                      fontWeight: 700,
                    }}
                  >
                    BioCircuit
                  </Typography>
                  <Typography
                    variant="h6"
                    component="span"
                    sx={{ 
                      fontFamily: 'Barlow, sans-serif',
                      color: '#A8CF45',
                      fontWeight: 700,
                      marginLeft: '8px'
                    }}
                  >
                    LABS
                  </Typography>
                </Box>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontFamily: 'Quicksand, sans-serif',
                  mb: 2,
                  lineHeight: 1.7
                }}
              >
                Transforming healthcare operations through innovative technology solutions.
              </Typography>
            </Box>

            {/* Quick Links */}
            <Box sx={{ flex: 0.6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { text: 'Home', path: '/' },
                  { text: 'Careers', path: '/careers' },
                  { text: 'Contact', path: '/contact' }
                ].map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.path}
                      style={{ 
                        textDecoration: 'none',
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#51A8B1'}
                      onMouseLeave={(e) => e.target.style.color = '#666'}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Legal */}
            <Box sx={{ flex: 0.6 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Legal
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { text: 'Privacy Policy', path: '/privacy-policy' },
                  { text: 'Terms & Conditions', path: '/terms-conditions' }
                ].map((item) => (
                  <motion.div
                    key={item.text}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={item.path}
                      style={{ 
                        textDecoration: 'none',
                        fontFamily: 'Quicksand, sans-serif',
                        color: '#666',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#51A8B1'}
                      onMouseLeave={(e) => e.target.style.color = '#666'}
                    >
                      {item.text}
                    </Link>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Contact Info */}
            <Box sx={{ flex: 0.8 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Barlow, sans-serif',
                  color: '#51A8B1',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Contact Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                  <Typography
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      fontSize: '0.95rem'
                    }}
                  >
                    info@biocircuitlabs.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneIcon sx={{ color: '#51A8B1', fontSize: '1.2rem' }} />
                  <Typography
                    sx={{ 
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#666',
                      fontSize: '0.95rem'
                    }}
                  >
                    +91 94141 74048
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Bottom Bar */}
          <Divider sx={{ my: 4, borderColor: 'rgba(81, 168, 177, 0.1)' }} />
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Quicksand, sans-serif',
                color: '#666',
                textAlign: 'center'
              }}
            >
              © {new Date().getFullYear()} BioCircuit Labs. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Careers;